import { EventEmitter, Output } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EFormHelper } from '../../../helper/script/helper';
import { State } from '../../../model/eform-type';
import { Observable, Subscription } from 'rxjs';
import { EformAPI } from '../../../e-form.api';
export enum Address {
   Province = 'province',
   District = 'district',
   SubDistrict = 'subDistrict',
   ZipCode = 'zipCode',
   Address1 = 'address1',
   Address2 = 'address2',
   City = 'city',
   State = 'state',
   Postal = 'postal',
   Country = 'country',
   DefaultValue = 'defaultValue',
}

@Component({
   selector: 'app-address-properties',
   templateUrl: './address-properties.component.html',
   styleUrls: ['./address-properties.component.scss']
})

export class AddressPropertiesComponent implements OnInit, OnDestroy {
   @Input() eForm: FormGroup;
   @Input() state: State;
   @Input() table: any;
   @Input() clickEvents: EventEmitter<string>;
   @Output() sendFocus = new EventEmitter<boolean>();
   @Input() eventsStateChild: Observable<void>;
   Address = Address;
   helper = new EFormHelper();
   setEmpty = {code: '', id: '', name_th: '', name_en: '', name_cn: '', zip_code: ''};
   countries = [];
   provinces = [];
   districts = [];
   subDistricts = [];
   eventSub: Subscription;
   displayDisabled: boolean = false;
   constructor(private api: EformAPI, public translate: TranslateService) { }

   ngOnInit(): void {
      this.helper.checkDisplayDisabled(this.eForm, this.state, this.table);
      this.helper.displayDisabledChanges.subscribe((value) => {
         this.displayDisabled = value;
      });
      this.eventsStateChild.subscribe(() => {
         this.helper.checkDisplayDisabled(this.eForm, this.state, this.table);
      });
      this.onSetUpProvince();
      this.onSetUpCountry();
      this.onSet();
      this.eventSub = this.clickEvents.subscribe(() => {
         this.onSet();
     });
   }

   onSet() {
      if (this._getValue('type') === 'local') {
         const _provinceGroup = this.getField.get(Address.Province) as FormGroup;
         const _districtGroup = this.getField.get(Address.District) as FormGroup;
         const _province = _provinceGroup.controls.defaultValue.value;
         const _district = _districtGroup.controls.defaultValue.value;
         if (_province.code !== undefined) {
            this.onSetUpDistrict(_province, 'click');
         }
         if (_district.code !== undefined) {
            this.onSetUpSubDistrict(_district, 'click');
         }
      }
   }

   ngOnDestroy(): void {
      if (this.eventSub) {
         this.eventSub.unsubscribe();
      }
   }

   onSetUpCountry() {
      this.api.getCountry(this.eForm.controls.original.value).subscribe(res => {
         this.countries = res;
      }, err => { console.error(err), this.countries = []; });
   }

   onSetUpProvince() {
      this.api.getProvince(this.eForm.controls.original.value).subscribe(res => {
         this.provinces = res;
      }, err => { console.error(err), this.provinces = []; });
   }

   onSetUpDistrict(value: any, state: string) {
      this.subDistricts = [];
      if (state === 'select') {
         this.setField(Address.ZipCode).patchValue({code: '', name_th: '', name_en: '', name_cn: '', zip_code: ''});
         this.setField(Address.District).patchValue({code: '', name_th: '', name_en: '', name_cn: ''});
         this.setField(Address.SubDistrict).patchValue({code: '', name_th: '', name_en: '', name_cn: ''});
      }

      if (value.code !== '') {
         this.api.getDistrictWithProvince(value.code,this.eForm.controls.original.value).subscribe(res => {
            this.districts = res;
         }, err => { console.error(err), this.districts = []; });
      } else {
         this.districts = [];
      }
   }

   onSetUpSubDistrict(value: any, state: string) {
      if (state === 'select') {
         this.setField(Address.ZipCode).patchValue({code: '', name_th: '', name_en: '', name_cn: '', zip_code: ''});
         this.setField(Address.SubDistrict).patchValue({code: '', name_th: '', name_en: '', name_cn: ''});
      }

      if (value.code !== '') {
         this.api.getSubDistrictWithDistrict(value.code,this.eForm.controls.original.value).subscribe(res => {
            this.subDistricts = res;
         }, err => { console.log(err), this.subDistricts = []; });
       } else {
         this.subDistricts = [];
      }
   }

   onSetUpZipCode(value: any) {
      if (value.code !== '') {
         this.api.getZipCodeWithSubDistrict(value.code).subscribe(res => {
            this.setField(Address.ZipCode).patchValue({zip_code: res});
         });
       } else {
         this.setField(Address.ZipCode).patchValue({zip_code: ''});
       }
      this.helper.getFieldAsFormArray(this.eForm, this.state.sectionIndex).updateValueAndValidity();
   }

   onTypeChange(value: string) {
      const _field = this.getField;
      (value === 'international') ? this.ifInternational(_field) : this.ifLocal(_field);
      this._clear(_field, value);
      this.helper.getFieldAsFormArray(this.eForm, this.state.sectionIndex).updateValueAndValidity();
      this.helper.checkDisplayDisabled(this.eForm, this.state, this.table);
      this.eForm.markAllAsTouched();
   }

   ifInternational(_field: FormGroup) {
      _field.addControl(Address.City, this._newForm);
      _field.addControl(Address.State, this._newForm);
      _field.addControl(Address.Postal, this._newForm);
      _field.addControl(Address.Country, new FormGroup({
         include: new FormControl(this.displayDisabled ?? false),
         required: new FormControl(this.displayDisabled ?? false),
         defaultValue: new FormControl({id: '', name_th: '', name_en: '', name_cn: ''})
      }));
   }

   ifLocal(_field: FormGroup) {
      _field.addControl(Address.Province, this._newFormNotAddress);
      _field.addControl(Address.District, this._newFormNotAddress);
      _field.addControl(Address.SubDistrict, this._newFormNotAddress);
      _field.addControl(Address.ZipCode, this._newFormZipCodeNotAddress);
   }

   _clear(_field: FormGroup, condition: string) {
      if (condition === 'international') {
         _field.removeControl(Address.Province);
         _field.removeControl(Address.District);
         _field.removeControl(Address.SubDistrict);
         _field.removeControl(Address.ZipCode);
      } else {
         _field.removeControl(Address.City);
         _field.removeControl(Address.State);
         _field.removeControl(Address.Postal);
         _field.removeControl(Address.Country);
      }
   }

   onExculde(formType: string, checked: boolean) {
      const _enable = [];
      const _disable = [];
      if (!checked) {
         switch (formType) {
            case Address.Province : _disable.push(Address.Province, Address.District, Address.SubDistrict, Address.ZipCode); break;
            case Address.District :  _disable.push(Address.District, Address.SubDistrict, Address.ZipCode); break;
            case Address.SubDistrict :  _disable.push( Address.SubDistrict, Address.ZipCode); break;
            case Address.ZipCode : _disable.push(formType); break;
            case Address.Address1 : case Address.Address2 :  _disable.push(formType); break;
            case Address.City : _disable.push(formType); break;
            case Address.State : _disable.push(formType); break;
            case Address.Postal : _disable.push(formType); break;
            case Address.Country : _disable.push(formType); break;
         }
         _disable.forEach(control => { this.disableControl(control); });
      } else {
         switch (formType) {
            case Address.Province : _enable.push(formType); break;
            case Address.District : _enable.push(Address.Province, Address.District); break;
            case Address.SubDistrict : _enable.push(Address.Province, Address.District, Address.SubDistrict); break;
            case Address.ZipCode : _enable.push(Address.Province, Address.District, Address.SubDistrict, Address.ZipCode); break;
            case Address.Address1 : case Address.Address2 : _enable.push(formType); break;
            case Address.City : _enable.push(formType); break;
            case Address.State : _enable.push(formType); break;
            case Address.Postal : _enable.push(formType); break;
            case Address.Country : _enable.push(formType); break;
         }
         _enable.forEach(control => { this.enableControl(control); });
      }
      this.helper.getFieldAsFormArray(this.eForm, this.state.sectionIndex).updateValueAndValidity();
   }

   disableControl(formType: string) {
      const _formGroup  = this.getField.get(formType) as FormGroup;
         _formGroup.controls.required.disable();
         _formGroup.controls.required.setValue(false);
         _formGroup.controls.include.setValue(false);
         if (formType === Address.Address1 || formType === Address.Address2 ||
             formType === Address.City || formType === Address.State || formType === Address.Postal) {
            const  _tmpGroup = _formGroup.get(Address.DefaultValue) as FormGroup;
            _tmpGroup.controls.value.setValue('');
            _tmpGroup.controls.value.disable();
         } else if (formType !== Address.ZipCode) {
            _formGroup.controls.defaultValue?.setValue({id: '', name_th: '', name_en: '', name_cn: ''});
            _formGroup.controls.defaultValue?.disable();
         } else {
            _formGroup.controls.defaultValue?.setValue({zip_code: ''});
            _formGroup.controls.defaultValue?.disable();
         }
   }

   enableControl(formType: string) {
      const _formGroup = this.getField.get(formType) as FormGroup;
      _formGroup.controls.include.setValue(true);
      _formGroup.controls.required.enable();
      if (formType === Address.Address1 || formType === Address.Address2 ||
         formType === Address.City || formType === Address.State || formType === Address.Postal) {
         const _tmpGroup = _formGroup.get(Address.DefaultValue) as FormGroup;
         _tmpGroup.controls.value.setValue('');
         _tmpGroup.controls.value.enable();
      } else if (formType !== Address.ZipCode) {
         _formGroup.controls.defaultValue?.enable();
         if (_formGroup.controls.defaultValue.value.code === '') {
            _formGroup.controls.defaultValue?.setValue({ code: '', name_th: '', name_en: '' , name_cn: ''});
         }
      } else {
         _formGroup.controls.defaultValue?.enable();
         const _tmpSubDistrict = this.getField.get(Address.SubDistrict) as FormGroup;
         const _subDistrictValue = _tmpSubDistrict.get(Address.DefaultValue).value;
         if (_subDistrictValue.code) {
            _formGroup.controls.defaultValue?.setValue({ zip_code: this.onSetUpZipCode(_subDistrictValue) });
         } else {
            _formGroup.controls.defaultValue?.setValue({ zip_code: '' });
         }
      }
   }

   selectionChange(formType: string, checked: boolean) {
      const _enable = [];
      const _disable = [];
      const _controls = (control: string, isEnable: boolean) => {
         const _formGroup = this.getField.get(control) as FormGroup;
         if (isEnable) {
            _formGroup.controls.required.setValue(true);
         } else {
            _formGroup.controls.required.setValue(false);
         }
      };

      if (!checked) {
         switch (formType) {
            case Address.Province : _disable.push(Address.Province, Address.District, Address.SubDistrict, Address.ZipCode); break;
            case Address.District : _disable.push(Address.District, Address.SubDistrict, Address.ZipCode); break;
            case Address.SubDistrict : _disable.push(Address.SubDistrict, Address.ZipCode); break;
            case Address.ZipCode : _disable.push(formType); break;
         }
         _disable.forEach(control => { _controls(control, false); });
      } else {
         switch (formType) {
            case Address.Province : _enable.push(formType); break;
            case Address.District :  _enable.push(Address.Province, Address.District); break;
            case Address.SubDistrict : _enable.push(Address.Province, Address.District, Address.SubDistrict); break;
            case Address.ZipCode :  _enable.push(Address.Province, Address.District, Address.SubDistrict, Address.ZipCode); break;
         }
         _enable.forEach(control => { _controls(control, true); });
      }
   }

   get _newForm() {
      return new FormGroup({
         include: new FormControl(this.displayDisabled ?? false),
         required: new FormControl(this.displayDisabled ?? false),
         defaultValue: new FormGroup({
            value: new FormControl('')
         })
      });
   }

   get _newFormNotAddress() {
      return new FormGroup({
         include: new FormControl(this.displayDisabled ?? false),
         required: new FormControl(this.displayDisabled ?? false),
         defaultValue: new FormControl({code: '', name_th: '', name_en: '', name_cn: ''})
      });
   }

   get _newFormZipCodeNotAddress() {
      return new FormGroup({
         include: new FormControl(this.displayDisabled ?? false),
         required: new FormControl(this.displayDisabled ?? false),
         defaultValue: new FormControl({zip_code: ''})
      });
   }

   get getField() {
      return this.helper.getFieldAsFormGroupWithIndex(this.eForm, this.state.sectionIndex, this.state.fieldIndex);
   }

   _getValue(type: string) {
      return this.helper.getFieldSomeValue(this.eForm, type, this.state.sectionIndex, this.state.fieldIndex);
   }

   setField(type: string) {
      const _group =  this.getField.get(type) as FormGroup;
      return _group.controls.defaultValue;
   }

   objectComparisonFunction(option: any, value: any): boolean {
      return option.name_th === value.name_th;
    }

    sendFocusToCreate(focus: boolean) {
      this.sendFocus.emit(focus);
   }


}


